/**
 * CriterionEditorModal — add/edit one prescreen question in a modal that looks
 * and works like the form builder's QuestionEditorModal (owner ask
 * 2026-09-29): new questions start on the type-picker grid, then the details;
 * editing opens on the details with "← Change question type". Same sections,
 * same inputs, same shared parts (QuestionTypeSelector, ConditionBuilder,
 * EligibilityRuleBuilder, CalculationEditor), limited to what the prescreen
 * stores — no Help Text / Placeholder / Required / Min-Max (validateCriteria
 * has no such fields), and no review-flag rule (verdicts are pass / fail /
 * recruiter judgment). prescreenFormBridge translates the form vocabulary to
 * the prescreen's answer types and PASSING rule.
 *
 * The prescreen-only fields (protocol wording, inclusion/exclusion, category)
 * sit in a collapsed "Protocol details" section; a new question fills them in
 * itself ("Added manually", backend requires non-empty source_text).
 *
 * Edits are held locally and only committed to the draft on Save —
 * Cancel/Escape/overlay-click discards them. A stored rule the form vocabulary
 * cannot express exactly (an AI-drafted "> 18", an `exists`) is kept as-is
 * unless the author chooses to replace it; a rule the author never touched is
 * saved back byte-for-byte.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DraftCriterion, FormEligibilityRule, FormQuestionDef, FormShowIf, normalizeShowIf } from '../../types';
import Modal from '../forms/Modal';
import QuestionTypeSelector from '../forms/QuestionTypeSelector';
import ConditionBuilder from '../forms/ConditionBuilder';
import EligibilityRuleBuilder from '../forms/EligibilityRuleBuilder';
import CalculationEditor from '../forms/CalculationEditor';
import ProfileKeyPicker, { suggestValueTypeFor } from '../forms/ProfileKeyPicker';
import { CALC_PRESET_KEYS } from '../forms/formMeta';
import { Alert } from '../ui';
import {
  CriterionSibling, ExcludeRule, PRESCREEN_FORM_TYPES, answerTypeOf, excludeToRule, formTypeOf,
  prescreenExcludeOperators, ruleQuestionType, ruleToExclude,
} from './prescreenFormBridge';
import { ruleSummary } from './PrescreenCriterionCard';

export const CRITERION_CATEGORIES = [
  'age', 'demographics', 'diagnosis', 'medication', 'labs', 'procedure',
  'lifestyle', 'logistics', 'consent', 'washout', 'other',
];

const inputCls =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';

const isNumericForm = (t: string) => t === 'number' || t === 'height' || t === 'calculation';

/** A choice value no other option uses (values are stable: a label edit never rewrites one). */
const mintOptionValue = (options: { value: string }[]) => {
  const used = new Set(options.map((o) => o.value));
  for (let n = options.length + 1; ; n++) {
    const candidate = `option-${n}`;
    if (!used.has(candidate)) return candidate;
  }
};

interface CriterionEditorModalProps {
  isOpen: boolean;
  /** The criterion to edit — a freshly seeded one when adding. */
  criterion: DraftCriterion | null;
  /** true = adding (starts on the type grid; the parent appends on save). */
  isNew: boolean;
  /** The OTHER draft criteria — populates branching + calculation input pickers. */
  siblings: CriterionSibling[];
  onSave: (c: DraftCriterion) => void;
  onClose: () => void;
}

function EditorBody({
  criterion, isNew, siblings, onSave, onClose,
}: { criterion: DraftCriterion } & Omit<CriterionEditorModalProps, 'isOpen' | 'criterion'>) {
  const initialType = formTypeOf(criterion);
  const initialRule = ruleToExclude(criterion.rule, initialType, criterion.options || []);

  const [step, setStep] = useState<'type' | 'details'>(isNew ? 'type' : 'details');
  const [type, setType] = useState<string>(initialType);
  const [label, setLabel] = useState(criterion.display_question || '');
  const [options, setOptions] = useState<{ value: string; label: string }[]>(criterion.options || []);
  const [showIf, setShowIf] = useState<FormShowIf | null>(criterion.show_if || null);
  const [calculation, setCalculation] = useState<{ preset: string; inputs: Record<string, string> } | null>(
    criterion.calculation ? { preset: criterion.calculation.preset, inputs: { ...(criterion.calculation.inputs || {}) } } : null,
  );
  const [exclude, setExclude] = useState<ExcludeRule | null>(initialRule.exclude);
  // The stored rule, kept verbatim while the author hasn't touched eligibility
  // (and always, for a rule the exclude vocabulary cannot express exactly).
  const [keptRule, setKeptRule] = useState<any>(criterion.rule || {});
  const [ruleTouched, setRuleTouched] = useState(false);
  const [profileKey, setProfileKey] = useState(criterion.profile_key_suggestion || '');
  const [sourceText, setSourceText] = useState(criterion.source_text || '');
  const [kind, setKind] = useState(criterion.criterion_kind || 'inclusion');
  const [category, setCategory] = useState(criterion.category || 'other');
  const [showProtocol, setShowProtocol] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCalc = type === 'calculation';
  const needsOptions = type === 'single_choice';
  // An untouched rule that has no exact exclude form stays as-is.
  const legacyRule = !ruleTouched && !initialRule.exact;

  // Branching sources + calculation inputs, keyed by draft ref (frozen to
  // question_keys at publish). Height reads as a number (total inches) — the
  // number operators for branching, and every calc slot accepts number.
  const siblingQuestions = (): FormQuestionDef[] => siblings
    .filter((s): s is CriterionSibling & { ref: string } => Boolean(s.ref) && !s.calculation)
    .map((s) => ({
      key: s.ref,
      type: s.answer_type === 'height' ? 'number' : formTypeOf(s),
      label: s.display_question || '(untitled question)',
      options: s.options || undefined,
    }));

  const applyType = (next: string) => {
    const prev = type;
    setType(next);
    setStep('details');
    setError(null);
    if (next === 'single_choice') {
      setOptions((o) => (o.length > 0 ? o : [
        { value: 'option-1', label: 'Option 1' },
        { value: 'option-2', label: 'Option 2' },
      ]));
    } else {
      setOptions([]);
    }
    if (next === 'calculation') {
      // Default the preset from the label (BMI is the common case). A computed
      // value is never asked, so it can't be conditional (forms parity).
      const preset = /\bbmi\b|body[\s-]*mass[\s-]*index/i.test(label) ? 'bmi' : CALC_PRESET_KEYS[0];
      setCalculation((c) => c || { preset, inputs: {} });
      setShowIf(null);
      setProfileKey('');
    } else {
      setCalculation(null);
    }
    if (next === prev) return;
    // Realign eligibility to the new type (the form modal's rule): keep a
    // numeric rule across numeric types, clear anything else.
    const keepNumeric = isNumericForm(prev) && isNumericForm(next);
    if (!keepNumeric) {
      setExclude(null);
      setKeptRule({});
      setRuleTouched(true);
    } else if (exclude && !prescreenExcludeOperators(next).includes(exclude.operator)) {
      setExclude(null);
      setRuleTouched(true);
    }
  };

  const updateOptionLabel = (i: number, value: string) => {
    setOptions((prev) => prev.map((o, j) => (j === i ? { ...o, label: value } : o)));
  };
  const addOption = () => setOptions((prev) => [...prev, { value: mintOptionValue(prev), label: `Option ${prev.length + 1}` }]);
  const removeOption = (i: number) => {
    const removed = options[i]?.value;
    setOptions((prev) => prev.filter((_, j) => j !== i));
    // Drop the removed choice from the exclude rule so it never points at nothing.
    if (!exclude || removed === undefined) return;
    if (Array.isArray(exclude.value)) {
      const rest = exclude.value.filter((v: any) => String(v) !== removed);
      setExclude(rest.length ? { ...exclude, value: rest } : null);
      setRuleTouched(true);
    } else if (String(exclude.value) === removed) {
      setExclude(null);
      setRuleTouched(true);
    }
  };

  const onExcludeChange = (r: FormEligibilityRule | null) => {
    setExclude(r?.exclude_if || null);
    setRuleTouched(true);
  };

  const save = () => {
    const question = label.trim();
    if (!question) { setError(isCalc ? 'Give this calculation a label.' : 'Enter the question text.'); return; }
    const keptOptions = needsOptions ? options.filter((o) => (o.label || '').trim()) : null;
    if (needsOptions && (keptOptions || []).length < 2) { setError('Add at least 2 options.'); return; }

    // Every conditional-display condition needs a source and a value — the
    // check the server runs, surfaced here so it names the fix in place.
    if (!isCalc) {
      for (const cond of normalizeShowIf(showIf)?.conditions || []) {
        if (!cond.question_key) { setError('Pick a question for each display condition.'); return; }
        const v = cond.value;
        if (v === '' || v === undefined || v === null || (Array.isArray(v) && v.length === 0)) {
          setError('Give each display condition a value (or remove the condition).'); return;
        }
      }
    }

    // Rebuild the passing rule from what the editor shows once the author has
    // touched it, or when the choices changed under an exact rule (a stored
    // "passes when one of A, B" must let a newly added choice C through, as the
    // "not eligible if A or B" it is shown as says).
    const choicesChanged = needsOptions && initialRule.exact
      && JSON.stringify((keptOptions || []).map((o) => o.value)) !== JSON.stringify((criterion.options || []).map((o) => o.value));
    let rule: any = keptRule;
    if (ruleTouched || choicesChanged) {
      const res = excludeToRule(exclude, type, keptOptions || []);
      if ('error' in res) { setError(res.error); return; }
      rule = res.rule;
    }

    const calc = isCalc && calculation
      ? {
        preset: calculation.preset,
        inputs: Object.fromEntries(Object.entries(calculation.inputs || {}).filter(([, ref]) => ref)),
      }
      : null;

    onSave({
      ...criterion,
      display_question: question,
      answer_type: answerTypeOf(type),
      options: keptOptions,
      rule,
      calculation: calc,
      show_if: isCalc ? null : showIf,
      profile_key_suggestion: isCalc ? null : (profileKey.trim() || null),
      source_text: sourceText.trim() || 'Added manually',
      criterion_kind: kind,
      category,
    });
  };

  // Step 1: type picker (new questions only).
  if (step === 'type') {
    return (
      <div className="space-y-5">
        <p className="text-sm text-gray-500 dark:text-gray-400">Choose the type of question you want to add.</p>
        <QuestionTypeSelector onSelect={applyType} types={PRESCREEN_FORM_TYPES} />
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button type="button" onClick={() => setStep('type')} className="text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
        ← Change question type
      </button>

      {isCalc ? (
        <>
          <CalculationEditor
            config={calculation}
            questions={siblingQuestions()}
            onChange={(c) => setCalculation(c)}
          />
          <div>
            <label className={labelCls}>Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. BMI" className={inputCls} />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Shown to the recruiter with the computed value. It is never asked on the call.</p>
          </div>
        </>
      ) : (
        <>
          <div>
            <label className={labelCls}>Question <span className="text-red-500">*</span></label>
            <textarea rows={2} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Enter your question" className={inputCls} />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">The recruiter reads this out word for word on the call.</p>
          </div>

          {needsOptions && (
            <div>
              <label className={labelCls}>Options <span className="text-red-500">*</span></label>
              <div className="space-y-2">
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={o.label} onChange={(e) => updateOptionLabel(i, e.target.value)} placeholder="Option label" className={`${inputCls} flex-1`} />
                    <button type="button" onClick={() => removeOption(i)} className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" aria-label="Remove option">
                      ✕
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addOption} className="text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
                  + Add Option
                </button>
              </div>
            </div>
          )}

          {type === 'height' && (
            <p className="text-xs text-gray-500 dark:text-gray-400">The recruiter enters feet and inches; it is stored (and compared) as total inches.</p>
          )}

          <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
            <h4 className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Conditional Display (optional)</h4>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Only ask this question when a previous answer meets certain criteria. A skipped question never counts toward eligibility.</p>
            <ConditionBuilder condition={showIf} questions={siblingQuestions()} onChange={setShowIf} />
          </div>
        </>
      )}

      <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
        <h4 className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Eligibility Rules (optional)</h4>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Automatically determine eligibility based on this {isCalc ? 'calculated value' : 'answer'}. With no rule, the recruiter judges the answer.
        </p>
        {legacyRule ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              Current rule: <span className="font-medium">{ruleSummary(keptRule, answerTypeOf(type))}</span>
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This rule was written in a format this editor can&rsquo;t show, so it is kept exactly as it is.
            </p>
            <button
              type="button"
              onClick={() => { setExclude(null); setRuleTouched(true); }}
              className="mt-2 text-sm font-medium text-primary-700 hover:underline dark:text-primary-400"
            >
              Replace it with a new rule
            </button>
          </div>
        ) : (
          <EligibilityRuleBuilder
            rule={exclude ? { exclude_if: exclude, flag_for_review_if: null } : null}
            questionType={ruleQuestionType(type)}
            options={needsOptions ? options.filter((o) => (o.label || '').trim()) : []}
            onChange={onExcludeChange}
            excludeOnly
            operators={prescreenExcludeOperators(type)}
          />
        )}
      </div>

      {!isCalc && (
        <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
          <h4 className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Field tag (optional)</h4>
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            A shared cross-study tag (profile key) so this answer can be searched across forms and prescreens. The library lives under Settings → Field tags.
          </p>
          <div className="w-full sm:w-72">
            <ProfileKeyPicker
              value={profileKey}
              onChange={(k) => setProfileKey(k || '')}
              suggestValueType={suggestValueTypeFor(answerTypeOf(type))}
              suggestLabel={label.trim()}
              inputClassName={inputCls}
            />
          </div>
        </div>
      )}

      {/* Prescreen-only provenance, out of the way: filled in for new questions. */}
      <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setShowProtocol((v) => !v)}
          aria-expanded={showProtocol}
          className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100"
        >
          {showProtocol ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Protocol details (optional)
        </button>
        {!showProtocol && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {kind === 'exclusion' ? 'Exclusion' : 'Inclusion'} · {category.replace(/_/g, ' ')} · {sourceText.trim() ? `“${sourceText.trim().slice(0, 80)}${sourceText.trim().length > 80 ? '…' : ''}”` : 'Added manually'}
          </p>
        )}
        {showProtocol && (
          <div className="mt-3 space-y-4">
            <div>
              <label className={labelCls}>Protocol wording</label>
              <textarea rows={2} value={sourceText} onChange={(e) => setSourceText(e.target.value)} placeholder="Added manually" className={inputCls} />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">The inclusion/exclusion wording this question comes from, shown next to it as its source.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Inclusion or exclusion</label>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls}>
                  <option value="inclusion">Inclusion</option>
                  <option value="exclusion">Exclusion</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                  {CRITERION_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
        <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
          Cancel
        </button>
        <button type="button" onClick={save} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800">
          {isNew ? 'Add Question' : 'Update Question'}
        </button>
      </div>
    </div>
  );
}

export default function CriterionEditorModal({
  isOpen, criterion, isNew, siblings, onSave, onClose,
}: CriterionEditorModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isNew ? 'Add Question' : 'Edit Question'}>
      {/* Mounted only while open (Modal returns null when closed), so state
          initializes fresh from `criterion` each time the editor opens; the
          ref key remounts it when a different criterion is opened. */}
      {criterion && (
        <EditorBody
          key={criterion.ref ?? 'new'}
          criterion={criterion}
          isNew={isNew}
          siblings={siblings}
          onSave={onSave}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}
